import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { InputSearchWithSidebarContainer } from '../../../common/input-search-with-sidebar/input-search-with-sidebar.styles';
import { InputContainer } from '../../../common/form-field-text/form-field-text.styles';
import {
  ArrowIconContainer,
  InputSelect,
} from '../../../common/input-select-with-sidebar/input-select-with-sidebar.styles';
import { ReactComponent as ArrowRightIcon } from '../../../assets/arrow-right-icon.svg';
import {
  InputPlaceholderGrayDark,
  SidebarContentWrapper,
  InputWrapper,
} from './call-courier-with-sidebar.styles';
import Sidebar from '../../../common/sidebar-right/sidebar.component';
import { useStore } from '../../../stores/store';
import Spinner from '../../../common/spinner/spinner.component';
import Button from '../../../common/button/button.component';
import { TextLabel } from '../../../common/typography/typography.styles';
import {
  isCourierPickupEmpty,
  formatDisplayDate,
  formatDisplayTime,
  getNearestDate,
} from './helpers';
import { TimeSlot } from '../../../models/pickupWindowsModels';
import CallCourierForm from './call-courier-form.component';

const CallCourierWithSidebar = () => {
  const {
    navStore: { toggleSidebarByName },
    parcelCreationStore: {
      setCourierPickupValues,
      shipment,
      dateInfo,
      isLoadingApiCallCourier,
    },
  } = useStore();

  const { t } = useTranslation();

  const shipmentPickupInfo = shipment?.sender?.courier_pickup;

  const [sidebarPickupInfo, setSidebarPickupInfo] =
    useState(shipmentPickupInfo); // Defines what is selected in the opened sidebar

  useEffect(() => {
    if (!isCourierPickupEmpty(shipmentPickupInfo) || !dateInfo) return;

    const nearestDate = getNearestDate(dateInfo, t);

    if (!nearestDate) return;

    const info = {
      date: nearestDate.date,
      time_from: nearestDate.time_slots[0].from,
      time_to: nearestDate.time_slots[0].to,
    };

    setSidebarPickupInfo(info);

    setCourierPickupValues(info);
  }, [dateInfo, shipmentPickupInfo, t, setCourierPickupValues]);

  const handleDateChange = (date: string) => {
    if (!dateInfo) return;

    setSidebarPickupInfo((prev) => ({ ...prev, date }));
  };

  const handleTimeChange = ({ from, to }: TimeSlot) => {
    setSidebarPickupInfo((prev) => ({
      ...prev,
      time_from: from,
      time_to: to,
    }));
  };

  const handleChooseClick = () => {
    if (sidebarPickupInfo) {
      setCourierPickupValues(sidebarPickupInfo);
    }

    toggleSidebarByName('choose_date_time');
  };

  const displayDate = shipmentPickupInfo
    ? formatDisplayDate(shipmentPickupInfo.date!, t)
    : '';
  const displayTime = shipmentPickupInfo
    ? formatDisplayTime(
        shipmentPickupInfo.time_from!,
        shipmentPickupInfo.time_to!
      )
    : '';

  return (
    <InputSearchWithSidebarContainer $mb='0'>
      <InputWrapper>
        <TextLabel>{t('create_parcel_courier_courier_arrival')}</TextLabel>
        <InputContainer
          $error={false}
          $maxInputHeight='20rem'>
          <InputSelect
            tabIndex={0}
            onClick={() => toggleSidebarByName('choose_date_time')}>
            <ArrowIconContainer>
              <ArrowRightIcon />
            </ArrowIconContainer>
            {shipmentPickupInfo && (
              <InputPlaceholderGrayDark>
                {displayDate} {displayTime}
              </InputPlaceholderGrayDark>
            )}
          </InputSelect>
        </InputContainer>
      </InputWrapper>

      <Sidebar
        name='choose_date_time'
        sidebarWidth='48rem'
        withBlurredBackground
        onClose={() => setSidebarPickupInfo(shipmentPickupInfo)}
        header={t('create_parcel_courier_date_time')}>
        {isLoadingApiCallCourier ? (
          <Spinner />
        ) : (
          <SidebarContentWrapper>
            <CallCourierForm
              pickupSlots={dateInfo}
              isLoading={isLoadingApiCallCourier}
              selectedPickupInfo={sidebarPickupInfo}
              onDateChange={handleDateChange}
              onTimeChange={handleTimeChange}
            />
            <Button
              type='submit'
              $mt='2.4rem'
              onClick={handleChooseClick}>
              {t('choose')}
            </Button>
          </SidebarContentWrapper>
        )}
      </Sidebar>
    </InputSearchWithSidebarContainer>
  );
};

export default observer(CallCourierWithSidebar);
